import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import CFColorPicker from "../shared/forms/CFColorPicker";
import CFSelect from "../shared/forms/CFSelect";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import VariantBuilderPanelCollapse from "../variants/VariantBuilderPanelCollapse";
import VariantSettingsThemeContainer from "../variants/VariantSettingsThemeContainer";
import VariantSettingsThemeHeadings from "./VariantSettingsThemeHeadings";
import VariantSettingsThemeTimer from "./VariantSettingsThemeTimer";
import VariantSettingsThemeForms from "./VariantSettingsThemeForms";
import VariantSettingsThemeButtons from "./VariantSettingsThemeButtons";
import VariantSettingsThemeProgress from "./VariantSettingsThemeProgress";
import VariantSettingsThemeProducts from "./VariantSettingsThemeProducts";
import VariantSettingsThemeProductHero from "./VariantSettingsThemeProductHero";
import VariantSettingsThemeIcon from "./VariantSettingsThemeIcon";
import VariantSettingsThemeList from "./VariantSettingsThemeList";
import VariantSettingsThemeSurveys from "./VariantSettingsThemeSurveys";
import VariantSettingsThemeCollapses from "./VariantSettingsThemeCollapses";
import VariantSettingsThemeTabs from "./VariantSettingsThemeTabs";
import VariantSettingsThemeCarousel from "./VariantSettingsThemeCarousel";
import VariantSettingsThemeTicker from "./VariantSettingsThemeTicker";
import VariantSettingsThemeQuiz from "./VariantSettingsThemeQuiz";
import VariantBuilderPanelTitle from "../variants/VariantBuilderPanelTitle";
import VariantSettingsThemeClose from "../variants/VariantSettingsThemeClose";
import VariantSettingsThemeLogo from "./VariantSettingsThemeLogo";
import VariantSettingsThemeNavigation from "./VariantSettingsThemeNavigation";
import VariantSettingsThemeReviews from "./VariantSettingsThemeReviews";
import VariantSettingsThemeScheduler from "./VariantSettingsThemeScheduler";
import {
  brandRemixLightColorSettings,
  brandRemixDarkColorSettings,
} from "../../utils/brandRemixWebsiteTheme";

const VariantSettingsTheme = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const [showPresets, setShowPresets] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState(undefined);
  const [remixLoading, setRemixLoading] = useState(false);
  const [showRemixModal, setShowRemixModal] = useState(false);
  const current_user = contextData?.objects?.current_user;
  const canUseRemix =
    current_user?.super_admin == true || current_user?.beta == true;

  // Reset all state when theme tab is clicked
  useEffect(() => {
    const handleResetTheme = () => {
      setShowPresets(false);
      setSelectedThemeId(undefined);
      setRemixLoading(false);
      setShowRemixModal(false);
    };

    document.addEventListener("resetThemeComponent", handleResetTheme);

    return () => {
      document.removeEventListener("resetThemeComponent", handleResetTheme);
    };
  }, []);

  // Reset state when themeTabClickCount changes (when theme tab is clicked)
  useEffect(() => {
    if (props.themeTabClickCount > 0) {
      setShowPresets(false);
      setSelectedThemeId(undefined);
      setRemixLoading(false);
      setShowRemixModal(false);
    }
  }, [props.themeTabClickCount]);

  // Reset presets when panel is closed
  useEffect(() => {
    if (contextData.panel_closed) {
      setShowPresets(false);
    }
  }, [contextData.panel_closed]);

  const remix_mappings = props.remix_mappings;

  // Get brand colors and fonts from the website data
  const getBrandTheme = () => {
    const d = contextData.objects?.website?.data || {};
    const colors = brandRemixLightColorSettings(d);
    const brandHeadlineFont = d["brand_font_headline"];
    const brandParagraphFont = d["brand_font_paragraph"];
    const logoDark = d["logo-dark"];
    const logoIconDark = d["logo-icon-dark"];

    return {
      id: "brand-theme",
      name: "Brand Light Theme",
      description: "Your brand colors and fonts",
      isBrandTheme: true,
      preview: {
        colors: [
          colors["background-color"],
          colors["button-color"],
          colors["accent-color"],
          colors["heading-font-color"],
        ],
        primaryColor: colors["button-color"],
        backgroundColor: colors["background-color"],
      },
      settings: {
        "logo-style": logoDark ? "custom" : "dark",
        "logo-image": logoDark || "",
        "logo-icon": logoIconDark || "",
        "heading-font-family": brandHeadlineFont
          ? { value: brandHeadlineFont, label: brandHeadlineFont }
          : { value: "Inter", label: "Inter" },
        "paragraph-font-family": brandParagraphFont
          ? { value: brandParagraphFont, label: brandParagraphFont }
          : { value: "Inter", label: "Inter" },
        "button-text-color": "#ffffff",
        ...colors,
      },
      headingStyle: "HEADING",
      fontWeight: "700",
    };
  };

  const getBrandThemeDark = () => {
    const d = contextData.objects?.website?.data || {};
    const colors = brandRemixDarkColorSettings(d);
    const brandHeadlineFont = d["brand_font_headline"];
    const brandParagraphFont = d["brand_font_paragraph"];
    const logoLight = d["logo-light"];
    const logoIconLight = d["logo-icon-light"];

    return {
      id: "brand-theme-dark",
      name: "Brand Dark Theme",
      description: "Your brand colors and fonts",
      dark: true,
      preview: {
        colors: [
          colors["background-color"],
          colors["button-color"],
          colors["accent-color"],
          colors["heading-font-color"],
        ],
        primaryColor: colors["button-color"],
        backgroundColor: colors["background-color"],
      },
      settings: {
        "logo-style": logoLight ? "custom" : "light",
        "logo-image": logoLight || "",
        "logo-icon": logoIconLight || "",
        "heading-font-family": brandHeadlineFont
          ? { value: brandHeadlineFont, label: brandHeadlineFont }
          : { value: "Inter", label: "Inter" },
        "paragraph-font-family": brandParagraphFont
          ? { value: brandParagraphFont, label: brandParagraphFont }
          : { value: "Inter", label: "Inter" },
        "button-text-color": "#ffffff",
        ...colors,
      },
      headingStyle: "HEADING",
      fontWeight: "700",
    };
  };

  const savedRemixThemes = [
    {
      ...getBrandTheme(),
      name: "Remix — Light",
      isSavedRemixTheme: true,
    },
    {
      ...getBrandThemeDark(),
      name: "Remix — Dark",
      isSavedRemixTheme: true,
    },
  ];

  const libraryPresetThemes = [
    {
      id: "blue",
      name: "Heading",
      description: "Blue theme",
      preview: {
        colors: ["#e0f2fe", "#b3e5fc", "#4fc3f7", "#0d47a1"],
        primaryColor: "#4fc3f7",
        backgroundColor: "#ffffff",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": { value: "Geologica", label: "Geologica" },
        "paragraph-font-family": { value: "Inter", label: "Inter" },
        "heading-font-color": "#000000",
        "paragraph-font-color": "#000000",
        "button-color": "#4fc3f7",
        "button-text-color": "#ffffff",
        "accent-color": "#4fc3f7",
        "background-color": "#ffffff",
      },
      headingStyle: "Heading",
      fontWeight: "700",
    },
    {
      id: "elegant",
      name: "Heading",
      description: "Elegant theme",
      preview: {
        colors: ["#f3e5f5", "#ce93d8", "#ab47bc", "#4a148c"],
        primaryColor: "#ab47bc",
        backgroundColor: "#ffffff",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": { value: "Georgia", label: "Georgia" },
        "paragraph-font-family": { value: "DM Sans", label: "DM Sans" },
        "heading-font-color": "#000000",
        "paragraph-font-color": "#424242",
        "button-color": "#ab47bc",
        "button-text-color": "#ffffff",
        "accent-color": "#ab47bc",
        "background-color": "#ffffff",
      },
      headingStyle: "Heading",
      fontWeight: "400",
    },
    {
      id: "warm",
      name: "Heading",
      description: "Warm theme",
      preview: {
        colors: ["#fff8e1", "#ffcc02", "#ff9800", "#e65100"],
        primaryColor: "#ff9800",
        backgroundColor: "#ffffff",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": { value: "Inter", label: "Inter" },
        "paragraph-font-family": { value: "Inter", label: "Inter" },
        "heading-font-color": "#000000",
        "paragraph-font-color": "#424242",
        "button-color": "#ff9800",
        "button-text-color": "#ffffff",
        "accent-color": "#ff9800",
        "background-color": "#ffffff",
      },
      headingStyle: "Heading",
      fontWeight: "600",
    },
    {
      id: "cool",
      name: "Heading",
      description: "Cool theme",
      preview: {
        colors: ["#f1f8e9", "#aed581", "#689f38", "#33691e"],
        primaryColor: "#689f38",
        backgroundColor: "#ffffff",
      },
      settings: {
        "logo-style": "dark",
        "heading-font-family": { value: "Inter", label: "Inter" },
        "paragraph-font-family": { value: "Inter", label: "Inter" },
        "heading-font-color": "#000000",
        "paragraph-font-color": "#424242",
        "button-color": "#689f38",
        "button-text-color": "#ffffff",
        "accent-color": "#689f38",
        "background-color": "#ffffff",
      },
      headingStyle: "Heading",
      fontWeight: "500",
    },
  ];

  const handleThemeSelect = (theme) => {
    setSelectedThemeId(theme.id);

    // Apply theme settings using remix_mappings
    if (remix_mappings && theme.settings) {
      const updates = [];

      // Iterate through each parent key in theme settings
      Object.keys(theme.settings).forEach((parentKey) => {
        let value = theme.settings[parentKey];

        // For font families, extract the value string from the object
        if (
          parentKey.includes("font-family") &&
          value &&
          typeof value === "object" &&
          value.value
        ) {
          value = value.value;
        }

        // Check if this parent key exists in remix_mappings
        if (remix_mappings[parentKey]) {
          // Apply the value to all child attributes mapped to this parent key
          remix_mappings[parentKey].forEach((childAttribute) => {
            updates.push({
              object_type: "variants",
              setting_name: `[options][${childAttribute}]`,
              value: value,
            });
          });
        }

        // Handle background-color separately since it's applied directly
        if (parentKey === "background-color" && value) {
          updates.push({
            object_type: "variants",
            setting_name: `[options][background-color]`,
            value: value,
          });
        }
      });

      console.log("Applying theme updates:", updates);
      builder.update(updates);

      // Load fonts if they were selected
      if (theme.settings["heading-font-family"]?.value) {
        ctas.builder.fonts.load_font_family(
          theme.settings["heading-font-family"].value
        );
      }
      if (theme.settings["paragraph-font-family"]?.value) {
        ctas.builder.fonts.load_font_family(
          theme.settings["paragraph-font-family"].value
        );
      }
    }
  };

  const handleShuffle = () => {
    const randomTheme =
      libraryPresetThemes[
        Math.floor(Math.random() * libraryPresetThemes.length)
      ];
    console.log("Shuffled to theme:", randomTheme);
    handleThemeSelect(randomTheme);
  };

  const handleRemix = () => {
    setShowRemixModal(true);
  };

  const RemixThemeModal = () => {
    // Get current theme values from variant settings
    const variant = contextData.objects.variant;
    const currentHeadingFont =
      variant.options?.["h1-font-family"] ||
      variant.options?.["heading-font-family"] ||
      "Inter";
    const currentParagraphFont =
      variant.options?.["p-font-family"] ||
      variant.options?.["paragraph-font-family"] ||
      "Inter";
    const currentHeadingColor =
      variant.options?.["h1-font-color"] ||
      variant.options?.["heading-font-color"] ||
      "#000000";
    const currentParagraphColor =
      variant.options?.["p-font-color"] ||
      variant.options?.["paragraph-font-color"] ||
      "#666666";
    const currentButtonColor = variant.options?.["button-color"] || "#0D42E4";
    const currentAccentColor = variant.options?.["accent-color"] || "#0D42E4";
    const currentBackgroundColor =
      variant.options?.["background-color"] || "#ffffff";
    const currentLogoStyle = variant.options?.["logo-style"] || "dark";
    const currentButtonTextColor =
      variant.options?.["button-text-color"] || "#ffffff";

    const [importUrl, setImportUrl] = useState("");
    const [importLoading, setImportLoading] = useState(false);
    const [headingFont, setHeadingFont] = useState({
      value: currentHeadingFont,
      label: currentHeadingFont,
    });
    const [paragraphFont, setParagraphFont] = useState({
      value: currentParagraphFont,
      label: currentParagraphFont,
    });
    const [headingColor, setHeadingColor] = useState(currentHeadingColor);
    const [paragraphColor, setParagraphColor] = useState(currentParagraphColor);
    const [buttonColor, setButtonColor] = useState(currentButtonColor);
    const [buttonTextColor, setButtonTextColor] = useState(
      currentButtonTextColor
    );
    const [accentColor, setAccentColor] = useState(currentAccentColor);
    const [backgroundColor, setBackgroundColor] = useState(
      currentBackgroundColor
    );
    const [logoStyle, setLogoStyle] = useState({
      value: currentLogoStyle,
      text:
        currentLogoStyle === "dark"
          ? "Dark Brand Style"
          : currentLogoStyle === "light"
          ? "Light Brand Style"
          : "Custom Image Upload",
    });

    const handleImport = async () => {
      if (!importUrl.trim()) {
        alert("Please enter a valid URL");
        return;
      }

      setImportLoading(true);

      try {
        const response = await fetch(
          `/websites/${contextData.objects.website.id}/cta/${contextData.objects.cta.id}/variants/${contextData.objects.variant.id}/import_theme_from_url`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token":
                document
                  .querySelector('meta[name="csrf-token"]')
                  ?.getAttribute("content") || "",
            },
            body: JSON.stringify({ url: importUrl }),
          }
        );

        const data = await response.json();

        if (response.ok && data.styles) {
          // Apply the detected styles to the form
          const styles = data.styles;

          if (styles["heading-font-family"]) {
            const headingFontObj = {
              value: styles["heading-font-family"],
              label: styles["heading-font-family"],
            };
            setHeadingFont(headingFontObj);
            // Load the font
            if (window.ctas?.builder?.fonts?.load_font_family) {
              ctas.builder.fonts.load_font_family(
                styles["heading-font-family"]
              );
            }
          }

          if (styles["paragraph-font-family"]) {
            const paragraphFontObj = {
              value: styles["paragraph-font-family"],
              label: styles["paragraph-font-family"],
            };
            setParagraphFont(paragraphFontObj);
            // Load the font
            if (window.ctas?.builder?.fonts?.load_font_family) {
              ctas.builder.fonts.load_font_family(
                styles["paragraph-font-family"]
              );
            }
          }

          if (styles["heading-font-color"]) {
            setHeadingColor(styles["heading-font-color"]);
          }

          if (styles["paragraph-font-color"]) {
            setParagraphColor(styles["paragraph-font-color"]);
          }

          if (styles["button-color"]) {
            setButtonColor(styles["button-color"]);
          }

          if (styles["button-text-color"]) {
            setButtonTextColor(styles["button-text-color"]);
          } else if (styles["button-color"]) {
            // Default to white text if button color is set but no text color specified
            setButtonTextColor("#ffffff");
          }

          if (styles["background-color"]) {
            setBackgroundColor(styles["background-color"]);
          }

          console.log("Successfully imported theme styles:", styles);
        } else {
          throw new Error(data.error || "Failed to import theme");
        }
      } catch (error) {
        console.error("Error importing theme:", error);
        alert(
          "Failed to import theme from URL. Please check the URL and try again."
        );
      } finally {
        setImportLoading(false);
      }
    };

    const handleRemixThemeStyles = () => {
      const customTheme = {
        "heading-font-family": headingFont,
        "paragraph-font-family": paragraphFont,
        "heading-font-color": headingColor,
        "paragraph-font-color": paragraphColor,
        "button-color": buttonColor,
        "button-text-color": buttonTextColor,
        "accent-color": accentColor,
        "background-color": backgroundColor,
        "logo-style": logoStyle?.value || logoStyle,
      };

      console.log("Remixing theme with styles:", customTheme);

      // Apply theme settings using remix_mappings
      if (remix_mappings && customTheme) {
        const updates = [];

        // Iterate through each parent key in theme settings
        Object.keys(customTheme).forEach((parentKey) => {
          let value = customTheme[parentKey];

          // Skip empty values
          if (!value) return;

          // For font families, extract the value string from the object
          if (
            parentKey.includes("font-family") &&
            value &&
            typeof value === "object" &&
            value.value
          ) {
            value = value.value;
          }

          // Check if this parent key exists in remix_mappings
          if (remix_mappings[parentKey]) {
            // Apply the value to all child attributes mapped to this parent key
            remix_mappings[parentKey].forEach((childAttribute) => {
              updates.push({
                object_type: "variants",
                setting_name: `[options][${childAttribute}]`,
                value: value,
              });
            });
          }

          // Handle background-color separately since it's applied directly
          if (parentKey === "background-color" && value) {
            updates.push({
              object_type: "variants",
              setting_name: `[options][background-color]`,
              value: value,
            });
          }

          // Handle button-text-color separately since it's applied directly
          if (parentKey === "button-text-color" && value) {
            updates.push({
              object_type: "variants",
              setting_name: `[options][button-text-color]`,
              value: value,
            });
          }

          // If button-color is set but no button-text-color, default to white
          if (
            parentKey === "button-color" &&
            value &&
            !customTheme["button-text-color"]
          ) {
            updates.push({
              object_type: "variants",
              setting_name: `[options][button-text-color]`,
              value: "#ffffff",
            });
          }
        });

        console.log("Applying custom theme updates:", updates);
        builder.update(updates);

        // Load fonts if they were selected
        if (headingFont?.value) {
          ctas.builder.fonts.load_font_family(headingFont.value);
        }
        if (paragraphFont?.value) {
          ctas.builder.fonts.load_font_family(paragraphFont.value);
        }
      }

      setShowRemixModal(false);
    };

    if (!showRemixModal) return null;

    return (
      <div
        className="modal"
        style={{
          display: "block",
          background: "rgba(0, 0, 0, 0.5)",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1050,
          overflowY: "auto",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowRemixModal(false);
          }
        }}
      >
        <div
          className="modal-dialog"
          style={{
            margin: "100px auto",
            maxWidth: "500px",
            width: "90%",
          }}
        >
          <div className="modal-content rounded-xl shadow-lg dark:bg-slate-950">
            <div className="modal-header">
              <button
                type="button"
                className="close dark:text-white"
                data-dismiss={"modal-" + props.setting_name}
                aria-label="Close"
                onClick={() => {
                  setShowRemixModal(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                >
                  <path
                    d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="modal-body p-6">
              <div className="col-xs-12 mb-6">
                {/* URL Import Section */}
                {canUseRemix && (
                  <div
                    className="row hard-center p-5 mb-6 cf-remix-content w-full dark:bg-gray-900 rounded-lg border border-transparent overflow-hidden"
                    style={{
                      background: contextData.objects.website.dark_mode
                        ? "#000"
                        : `linear-gradient(90deg, rgba(107, 140, 255, 0.05) 0%, rgba(0, 63, 255, 0.05) 100%)`,
                    }}
                  >
                    <p className="m-0 mb-4 text-black dark:text-white">
                      Import theme from URL
                    </p>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={importUrl}
                        onChange={(e) => setImportUrl(e.target.value)}
                        className="flex-1 p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
                        placeholder="https://example.com"
                        disabled={importLoading}
                      />
                      <button
                        onClick={handleImport}
                        disabled={importLoading}
                        className="import-theme-btn px-6 py-3 text-xl font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                      >
                        {importLoading ? (
                          <span className="cf-loading-spinner" />
                        ) : (
                          <svg
                            width="19"
                            height="19"
                            className=""
                            viewBox="0 0 19 19"
                            fill="none"
                          >
                            <path
                              d="M11.6184 2.30005L12.9911 6.00971L16.7008 7.3824L12.9911 8.7551L11.6184 12.4648L10.2457 8.7551L6.53608 7.3824L10.2457 6.00971L11.6184 2.30005Z"
                              stroke="white"
                              strokeWidth="1.3"
                              strokeLinejoin="round"
                              fill="white"
                            />
                            <path
                              d="M5.26549 10.7706L6.46461 12.5362L8.23019 13.7353L6.46461 14.9345L5.26549 16.7L4.06637 14.9345L2.30078 13.7353L4.06637 12.5362L5.26549 10.7706Z"
                              stroke="white"
                              strokeWidth="1.3"
                              strokeLinejoin="round"
                              fill="white"
                            />
                          </svg>
                        )}
                        {importLoading ? "Importing..." : "Import"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Custom Theme Inputs */}
                <div className="row hard-center space-y-5">
                  {/* Font Selects */}
                  <div className="flex gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Heading Font
                        </p>
                      </div>
                      <CFSelect
                        className="smart-select"
                        name="heading-font-family"
                        font_dropdown={true}
                        options={[
                          {
                            value: "",
                            text: "Select...",
                            group: "System fonts",
                          },
                          ...(window?.builder_fonts_dropdown || []),
                        ]}
                        value={headingFont}
                        onChange={(fontFamily) => {
                          setHeadingFont(fontFamily);
                          if (fontFamily.value) {
                            ctas.builder.fonts.load_font_family(
                              fontFamily.value
                            );
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Paragraph Font
                        </p>
                      </div>
                      <CFSelect
                        className="smart-select"
                        name="paragraph-font-family"
                        font_dropdown={true}
                        options={[
                          {
                            value: "",
                            text: "Select...",
                            group: "System fonts",
                          },
                          ...(window?.builder_fonts_dropdown || []),
                        ]}
                        value={paragraphFont}
                        onChange={(fontFamily) => {
                          setParagraphFont(fontFamily);
                          if (fontFamily.value) {
                            ctas.builder.fonts.load_font_family(
                              fontFamily.value
                            );
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Color Inputs */}
                  <div className="flex gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Heading Color
                        </p>
                      </div>
                      <CFColorPicker
                        name="heading-font-color"
                        placeholder="Select"
                        website={contextData.objects.website}
                        value={headingColor}
                        onChange={(value) => {
                          setHeadingColor(value);
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Paragraph Color
                        </p>
                      </div>
                      <CFColorPicker
                        name="paragraph-font-color"
                        placeholder="Select"
                        website={contextData.objects.website}
                        value={paragraphColor}
                        onChange={(value) => {
                          setParagraphColor(value);
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Button Color
                        </p>
                      </div>
                      <CFColorPicker
                        name="button-color"
                        placeholder="Select"
                        website={contextData.objects.website}
                        value={buttonColor}
                        onChange={(value) => {
                          setButtonColor(value);
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Button Text
                        </p>
                      </div>
                      <CFColorPicker
                        name="button-text-color"
                        placeholder="Select"
                        website={contextData.objects.website}
                        value={buttonTextColor}
                        onChange={(value) => {
                          setButtonTextColor(value);
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Accents
                        </p>
                      </div>
                      <CFColorPicker
                        name="accent-color"
                        placeholder="Select"
                        website={contextData.objects.website}
                        value={accentColor}
                        onChange={(value) => {
                          setAccentColor(value);
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="mb-[5px] col-xs-9 no-padding-sides">
                        <p className="text-lg font-medium mb-2 text-black dark:text-white">
                          Background
                        </p>
                      </div>
                      <CFColorPicker
                        name="background-color"
                        placeholder="Select"
                        website={contextData.objects.website}
                        value={backgroundColor}
                        onChange={(value) => {
                          setBackgroundColor(value);
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-6 pt-5">
                    <button
                      onClick={handleRemixThemeStyles}
                      className="btn-block text-center text-white flex items-center justify-center import-theme-btn py-5 text-xl rounded-lg box-sizing-border-box"
                    >
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="mr-2"
                      >
                        <path
                          d="M11.6184 2.30005L12.9911 6.00971L16.7008 7.3824L12.9911 8.7551L11.6184 12.4648L10.2457 8.7551L6.53608 7.3824L10.2457 6.00971L11.6184 2.30005Z"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinejoin="round"
                          fill="white"
                        />
                        <path
                          d="M5.26549 10.7706L6.46461 12.5362L8.23019 13.7353L6.46461 14.9345L5.26549 16.7L4.06637 14.9345L2.30078 13.7353L4.06637 12.5362L5.26549 10.7706Z"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinejoin="round"
                          fill="white"
                        />
                      </svg>
                      Remix Theme Styles
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ThemePresetCard = ({ theme, onSelect, isSelected = false }) => (
    <div
      className={`cursor-pointer border-solid border-[1px] rounded-lg overflow-hidden transition-all relative mb-3 ${
        isSelected ? "border-black" : "border-slate-200 hover:border-black"
      }`}
      onClick={() => onSelect(theme)}
    >
      <div
        className={`p-4 flex items-center justify-between ${
          theme.dark ? "bg-black" : "bg-white"
        }`}
      >
        <div className="flex-1">
          <div
            className="text-lg mb-1"
            style={{
              fontFamily:
                theme.settings["heading-font-family"]?.value || "Inter",
              fontWeight: theme.fontWeight || "700",
              color: theme.dark ? "#fff" : "#000",
            }}
          >
            {theme.headingStyle || "HEADING"}
          </div>
          <div
            className="text-sm"
            style={{
              fontFamily:
                theme.settings["paragraph-font-family"]?.value || "Inter",
              color: theme.dark ? "#fff" : "#666",
            }}
          >
            Paragraph
          </div>
        </div>

        <div className="flex items-center space-x-1 ml-4">
          {theme.preview.colors.map((color, index) => (
            <div
              key={index}
              className="w-6 h-6 rounded-sm"
              style={{ backgroundColor: color, border: "1px solid #f2f2f2" }}
            />
          ))}
        </div>
      </div>
      {isSelected && (
        <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
          Selected
        </div>
      )}
    </div>
  );

  if (showPresets) {
    return (
      <React.Fragment>
        <div className="row hard-center remix-editor">
          <VariantBuilderPanelTitle
            title={`Theme Styles`}
            tooltip="Make your funnel on-brand by adjusting your theme styles. Override theme styles by styling elements you add."
            tooltipLabel="Learn"
          />

          <div className="col-xs-12 no-padding-sides mb-4">

            <h3 className="text-lg font-medium mb-2">Saved Themes:</h3>
            <div className="space-y-2 mb-6">
              {savedRemixThemes.map((theme) => (
                <ThemePresetCard
                  key={theme.id}
                  theme={theme}
                  onSelect={handleThemeSelect}
                  isSelected={selectedThemeId === theme.id}
                />
              ))}
            </div>

            <h3 className="text-lg font-medium mb-2">Preset Themes:</h3>

            <div className="flex gap-2 mb-4">
              <button
                onClick={handleShuffle}
                className="flex-1 flex items-center justify-center gap-2 border-solid border-[1px] py-3 px-5 rounded-lg text-xl cursor-pointer text-black hover:text-blue-600 dark:text-gray-400 dark:hover:text-white bg-white hover:bg-blue-100 dark:bg-slate-950 dark:hover:bg-slate-800 border-slate-200 hover:border-blue-100 dark:border-slate-800"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M0.75 10.7216H2.61315C3.33979 10.7216 4.0217 10.3707 4.44405 9.77943L5.08372 8.88388M12.2849 2.81447H10.4217C9.69509 2.81447 9.01318 3.16539 8.59083 3.75668L7.875 4.75884M11.1863 1.16663L12.75 2.81447L11.1863 4.4623M11.1863 9.11095L12.75 10.7588L11.1863 12.4066M0.75 3.06887H2.61315C3.33979 3.06887 4.0217 3.41979 4.44405 4.01109L8.59083 9.81658C9.01318 10.4079 9.69509 10.7588 10.4217 10.7588H12.2849"
                    stroke={contextData.dark_mode ? "#fff" : "black"}
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Shuffle
              </button>

              <button
                onClick={handleRemix}
                className="import-theme-btn text-white rounded-lg border-none !text-xl flex-1 items-center justify-center"
              >
                {remixLoading ? (
                  <span className="cf-loading-spinner" />
                ) : (
                  <React.Fragment>
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2 mb-[-5px]"
                    >
                      <path
                        d="M11.6184 2.30005L12.9911 6.00971L16.7008 7.3824L12.9911 8.7551L11.6184 12.4648L10.2457 8.7551L6.53608 7.3824L10.2457 6.00971L11.6184 2.30005Z"
                        stroke="white"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.26549 10.7706L6.46461 12.5362L8.23019 13.7353L6.46461 14.9345L5.26549 16.7L4.06637 14.9345L2.30078 13.7353L4.06637 12.5362L5.26549 10.7706Z"
                        stroke="white"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Remix
                  </React.Fragment>
                )}
              </button>
            </div>

            <div className="space-y-2">
              {libraryPresetThemes.map((theme) => (
                <ThemePresetCard
                  key={theme.id}
                  theme={theme}
                  onSelect={handleThemeSelect}
                  isSelected={selectedThemeId === theme.id}
                />
              ))}
            </div>
          </div>
          <RemixThemeModal />
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="row hard-center">
        <VariantBuilderPanelTitle
          title={`Theme Styles`}
          tooltip="Make your funnel on-brand by adjusting your theme styles. Override theme styles by styling elements you add."
          tooltipLabel="Learn"
        />

        <div className="col-xs-12 no-padding-sides mb-4 mt-[20px]">
          <button
            onClick={() => setShowPresets(true)}
            className="w-full text-center gap-2 border-solid border-[1px] py-3 px-5 rounded-lg text-xl ml-1 mr-1 cursor-pointer text-black hover:text-blue-600 dark:text-gray-400 dark:hover:text-white bg-white hover:bg-blue-100 dark:bg-slate-950 dark:hover:bg-slate-800 border-slate-200 hover:border-blue-100 dark:border-slate-800"
          >
            <svg
              className="mb-[-4px] mr-2"
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="17"
              viewBox="0 0 19 19"
              fill="none"
            >
              <path
                d="M12.1996 11.3001C11.7978 12.5573 10.7878 13.5627 9.49961 14.0001M9.49965 16.7001C6.52211 16.7001 4.09961 14.4185 4.09961 11.6141C4.09961 7.70005 9.49969 2.30005 9.49969 2.30005C9.49969 2.30005 14.8996 7.70005 14.8996 11.6141C14.8996 14.4185 12.4772 16.7001 9.49965 16.7001Z"
                stroke={contextData.dark_mode ? "#fff" : "black"}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Apply New Theme
          </button>
        </div>

        <VariantSettingsThemeContainer {...props} />
        <VariantSettingsThemeClose {...props} />
        <VariantSettingsThemeLogo {...props} />
        <VariantSettingsThemeNavigation {...props} />
        <VariantSettingsThemeHeadings {...props} />
        <VariantSettingsThemeForms {...props} />
        <VariantSettingsThemeButtons {...props} />
        <VariantSettingsThemeProducts {...props} />
        <VariantSettingsThemeProductHero {...props} />
        <VariantSettingsThemeSurveys {...props} />
        <VariantSettingsThemeQuiz {...props} />
        <VariantSettingsThemeProgress {...props} />
        <VariantSettingsThemeTimer {...props} />
        <VariantSettingsThemeIcon {...props} />
        <VariantSettingsThemeCollapses {...props} />
        <VariantSettingsThemeList {...props} />
        <VariantSettingsThemeTabs {...props} />
        <VariantSettingsThemeCarousel {...props} />
        <VariantSettingsThemeTicker {...props} />
        <VariantSettingsThemeReviews {...props} />
        <VariantSettingsThemeScheduler {...props} />
      </div>
      <RemixThemeModal />
    </React.Fragment>
  );
};

export default VariantSettingsTheme;
